import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Shield, DollarSign } from 'lucide-react';
import { formatNumber, formatCurrency } from '../../utils/Numberformat';
import StockService from '../../Api_Module/Api_Services/StockService';
import { useTheme } from '../ThemeContext';

export const CompanyHeader: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const {
    currentSymbol,
    currentCompanyName,
    currentSymbolImage,
    marketCurrentPrice,
    marketData,
  } = useSelector((state: any) => state.app || {});


  const name = currentCompanyName || marketData?.name || '—';
  const symbol = currentSymbol || marketData?.symbol || '';
  const exchange = marketData?.exchangeShortName || marketData?.exchange || '';
  const currency = marketData?.currency || 'USD';
  const price = marketData?.price ?? marketCurrentPrice;
  const change = marketData?.change ?? marketData?.changes ?? null;
  const changePct = marketData?.changesPercentage ?? marketData?.changePercent ?? null;
  // Prefer direct market cap fields (match previous summary panel behavior)
  const marketCapDirectRaw = (
    marketData?.marketCap ??
    marketData?.market_cap ??
    marketData?.market_capitalization ??
    marketData?.marketCapitalization ??
    marketData?.mktCap ??
    null
  );
  // If provider ships a preformatted string under another key, prefer it
  const marketCapFormattedAlt = (
    marketData?.marketCapFormatted ??
    marketData?.market_cap_formatted ??
    marketData?.marketCapStr ??
    marketData?.market_cap_str ??
    null
  );
  // Create a display string that mirrors the summary panel style (no currency suffix)
  let marketCapDisplay: string = '—';
  if (typeof marketCapDirectRaw === 'number' && isFinite(marketCapDirectRaw)) {
    // Heuristic: some providers send market cap in billions (e.g., 1131.62 => 1,131.62B)
    if (marketCapDirectRaw < 1_000_000) {
      const billionsStr = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(marketCapDirectRaw);
      marketCapDisplay = `${billionsStr}B`;
    } else {
      marketCapDisplay = formatNumber(marketCapDirectRaw);
    }
  } else if (typeof marketCapDirectRaw === 'string') {
    const raw = marketCapDirectRaw.trim();
    // If the string contains only digits, commas, or dots, parse and format; otherwise, render as-is (e.g., '1,131.62B')
    if (/^[\d,.]+$/.test(raw)) {
      const parsed = parseFloat(raw.replace(/,/g, ''));
      if (!isNaN(parsed) && isFinite(parsed)) {
        if (parsed < 1_000_000) {
          // Interpret as billions value
          const billionsStr = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(parsed);
          marketCapDisplay = `${billionsStr}B`;
        } else {
          marketCapDisplay = formatNumber(parsed);
        }
      } else {
        marketCapDisplay = '—';
      }
    } else {
      marketCapDisplay = raw;
    }
  } else if (typeof marketCapFormattedAlt === 'string' && marketCapFormattedAlt.trim()) {
    marketCapDisplay = marketCapFormattedAlt.trim();
  }
  // market state removed from UI

  const isUp = typeof change === 'number' ? change >= 0 : typeof changePct === 'number' ? changePct >= 0 : true;

  // Blended next-year price target state (autoPe removed; using true constant)
  const [ptData, setPtData] = useState<any | null>(null);
  const [ptError, setPtError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPriceTarget = async () => {
      if (!symbol) {
        setPtData(null);
        return;
      }
      try {
        setPtError(null);
        const data = await StockService.getNextYearPriceTarget(symbol);
        setPtData(data || null);
      } catch (e: any) {
        setPtError('Unable to load price target');
        setPtData(null);
      }
    };
    fetchPriceTarget();
  }, [symbol]);

  // Removed Explain/Tab logic; pills are informational only

  // Derived UI helpers - use consensus block directly from API response
  const consensus = ptData?.consensus;
  const gradesConsensus = ptData?.gradesConsensus;
  const numberOfAnalysts = consensus?.numberOfAnalysts || consensus?.analystCount || 35; // fallback to example value

  // Calculate total ratings count from gradesConsensus if available
  const totalRatings = gradesConsensus
    ? (gradesConsensus.strongBuy || 0) + (gradesConsensus.buy || 0) + (gradesConsensus.hold || 0) + (gradesConsensus.sell || 0) + (gradesConsensus.strongSell || 0)
    : numberOfAnalysts;

  // Current stock price from leveredDcf block or marketData
  const stockPrice: number | null = (() => {
    if (typeof ptData?.leveredDcf?.["Stock Price"] === 'number') {
      return ptData.leveredDcf["Stock Price"] as number;
    }
    if (typeof price === 'number') {
      return price as number;
    }
    return null;
  })();

  // Delta calculator - FIXED: shows how much ABOVE or BELOW target you are
  // If current price > target → negative (overvalued vs target)
  // If current price < target → positive (undervalued vs target)
  const computeDelta = (target: number | null) => {
    if (typeof target === 'number' && typeof stockPrice === 'number' && target !== 0) {
      return ((target - stockPrice) / stockPrice) * 100;
    }
    return null;
  };

  // Simplified: Use Consensus as primary price target (most user-friendly)
  const primaryTarget = typeof consensus?.targetConsensus === 'number' ? consensus.targetConsensus : null;
  const targetHigh = typeof consensus?.targetHigh === 'number' ? consensus.targetHigh : null;
  const targetLow = typeof consensus?.targetLow === 'number' ? consensus.targetLow : null;
  const targetMedian = typeof consensus?.targetMedian === 'number' ? consensus.targetMedian : null;

  // Delta for primary target
  const primaryDelta = computeDelta(primaryTarget);
  const formatDelta = (d: number | null) => d === null ? '—' : `${d > 0 ? '+' : ''}${d.toFixed(2)}%`;

  return (
    <div
      className={`w-full bg-theme-secondary/95 backdrop-blur shadow-sm`}
      role="region"
      aria-label="Company overview bar"
    >
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-5 lg:px-6 py-1">
        <div className="flex items-center justify-between gap-2.5">
          {/* Left: logo + name + symbol */}
          <div className="flex items-center gap-2 min-w-0">
            {currentSymbolImage ? (
              <img src={currentSymbolImage} alt={`${name} logo`} className="h-6 w-6 rounded-md object-contain ring-1 ring-theme-tertiary/30" />
            ) : (
              <Shield className="h-6 w-6 text-theme-muted" />
            )}
            <div className="min-w-0 leading-none">
              <div className="flex items-center gap-1.5">
                <h1 className="text-xs md:text-sm font-semibold text-theme-primary truncate leading-tight tracking-tight">{name}</h1>
                {symbol && (
                  <span className="text-[9px] md:text-[10px] px-1 py-0.5 rounded bg-theme-tertiary/60 text-theme-secondary uppercase tracking-wide font-medium leading-none">
                    {exchange ? `${exchange}:${symbol}` : symbol}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right: metrics cluster */}
          <div className="flex items-center gap-2 shrink-0">
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] md:text-[11px] bg-theme-tertiary/70 font-medium leading-tight ${isUp ? 'text-emerald-500' : 'text-rose-500'}`}>
              <span className="font-semibold text-theme-primary text-[12px] md:text-[13px] leading-none">{formatCurrency(price, currency)}</span>
              <span className="leading-none">
                {changePct !== null && changePct !== undefined ? (
                  `${isUp ? '▲' : '▼'} ${Number(changePct).toFixed(2)}%`
                ) : change !== null && change !== undefined ? (
                  `${isUp ? '+' : ''}${formatNumber(Number(change), 2)}`
                ) : (
                  '—'
                )}
              </span>
            </div>
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] md:text-[11px] bg-theme-tertiary/50 text-theme-secondary font-medium leading-tight">
              <DollarSign size={12} className="text-emerald-400" />
              <span className="text-theme-muted">Mkt Cap</span>
              <span className="text-theme-primary font-semibold leading-none">{marketCapDisplay}</span>
            </div>
          </div>
        </div>

        {/* Consensus Price Target */}
        <div className="mt-1.5 pb-0.5">
          <div className="mb-1 flex items-center justify-between gap-2">
            <div className="text-[10px] md:text-[11px] font-semibold text-theme-primary tracking-tight leading-tight">
              Analyst 12‑Month Price Target
            </div>
          </div>

          {ptError ? (
            <div className="py-1.5 px-2 rounded-md bg-rose-500/10 text-[10px] text-rose-400 leading-tight">
              {ptError}
            </div>
          ) : primaryTarget !== null ? (
            <div className="space-y-1.5">
              {/* Primary Target Card */}
              <div className={`flex flex-wrap items-center gap-3 p-2.5 rounded-lg ${theme === 'dark' ? 'bg-gray-800/40' : 'bg-gray-100'}`}>
                {/* Main Target */}
                <div className="flex items-baseline gap-2 relative">
                  <span className="text-xl md:text-2xl font-bold text-theme-primary tracking-tight leading-tight">
                    {formatCurrency(primaryTarget, currency)}
                  </span>
                  {primaryDelta !== null && (
                    <span className={`text-[9px] md:text-[10px] font-semibold px-1.5 py-0.5 rounded-md leading-none ${primaryDelta > 0
                      ? 'bg-emerald-500/15 text-emerald-400'
                      : 'bg-rose-500/15 text-rose-400'
                      }`}>
                      {primaryDelta > 0 ? '▲' : '▼'} {formatDelta(primaryDelta)}
                    </span>
                  )}
                </div>

                {/* Target Range */}
                <div className="flex items-center gap-4 text-[9px] md:text-[10px] font-medium leading-tight">
                  {targetLow !== null && (
                    <div className="flex flex-col gap-0.5 min-w-[2.75rem] leading-tight">
                      <span className="text-[8px] font-normal tracking-wide text-theme-muted">Low</span>
                      <span className="text-rose-400 leading-tight">{formatCurrency(targetLow, currency)}</span>
                    </div>
                  )}
                  {targetMedian !== null && (
                    <div className="flex flex-col gap-0.5 min-w-[2.75rem] leading-tight">
                      <span className="text-[8px] font-normal tracking-wide text-theme-muted">Median</span>
                      <span className="text-theme-primary leading-tight">{formatCurrency(targetMedian, currency)}</span>
                    </div>
                  )}
                  {targetHigh !== null && (
                    <div className="flex flex-col gap-0.5 min-w-[2.75rem] leading-tight">
                      <span className="text-[8px] font-normal tracking-wide text-theme-muted">High</span>
                      <span className="text-emerald-400 leading-tight">{formatCurrency(targetHigh, currency)}</span>
                    </div>
                  )}
                </div>

                {/* Analyst Count - Link to Details */}
                <button
                  type="button"
                  onClick={() => symbol && navigate(`/${symbol}/analyst-corner`)}
                  className="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-theme-tertiary/15 text-[9px] md:text-[10px] font-medium leading-tight hover:bg-theme-tertiary/25 text-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors group cursor-pointer"
                  title="View detailed analyst profiles, track records & news"
                  disabled={!symbol}
                >
                  <span className="text-theme-muted">{totalRatings} analyst{totalRatings !== 1 ? 's' : ''}</span>
                  <span className="text-[8px] text-blue-400 group-hover:text-blue-300 underline decoration-dotted transition-colors">Details →</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="py-2.5 px-2 rounded-md bg-theme-tertiary/15 text-[10px] text-center text-theme-secondary leading-tight">
              Price target data not available
            </div>
          )}

          {/* Analyst Ratings */}
          {gradesConsensus && (
            <div className="mt-2 pt-2 border-t border-theme-tertiary/30">
              <div className="flex items-center justify-between gap-3">
                {/* Left: Ratings Distribution */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[9px] md:text-[10px] font-medium text-theme-muted">Analyst Ratings</span>
                    {gradesConsensus.consensus && (
                      <span className={`text-[8px] md:text-[9px] font-semibold px-1.5 py-0.5 rounded-md leading-none ${gradesConsensus.consensus === 'Strong Buy' || gradesConsensus.consensus === 'Buy'
                        ? 'bg-emerald-500/15 text-emerald-400'
                        : gradesConsensus.consensus === 'Hold'
                          ? 'bg-amber-500/15 text-amber-400'
                          : 'bg-rose-500/15 text-rose-400'
                        }`}>
                        {gradesConsensus.consensus}
                      </span>
                    )}
                  </div>

                  {/* Visual Distribution Bar */}
                  {(() => {
                    const total = (gradesConsensus.strongBuy || 0) + (gradesConsensus.buy || 0) + (gradesConsensus.hold || 0) + (gradesConsensus.sell || 0) + (gradesConsensus.strongSell || 0);
                    if (total === 0) return null;

                    const strongBuyPct = ((gradesConsensus.strongBuy || 0) / total) * 100;
                    const buyPct = ((gradesConsensus.buy || 0) / total) * 100;
                    const holdPct = ((gradesConsensus.hold || 0) / total) * 100;
                    const sellPct = ((gradesConsensus.sell || 0) / total) * 100;
                    const strongSellPct = ((gradesConsensus.strongSell || 0) / total) * 100;

                    return (
                      <div className="space-y-1">
                        {/* Stacked bar */}
                        <div className="flex h-2 rounded-full overflow-hidden bg-theme-tertiary/20">
                          {strongBuyPct > 0 && (
                            <div
                              className="bg-emerald-500 transition-all"
                              style={{ width: `${strongBuyPct}%` }}
                              title={`Strong Buy: ${gradesConsensus.strongBuy}`}
                            />
                          )}
                          {buyPct > 0 && (
                            <div
                              className="bg-emerald-400/80 transition-all"
                              style={{ width: `${buyPct}%` }}
                              title={`Buy: ${gradesConsensus.buy}`}
                            />
                          )}
                          {holdPct > 0 && (
                            <div
                              className="bg-amber-400 transition-all"
                              style={{ width: `${holdPct}%` }}
                              title={`Hold: ${gradesConsensus.hold}`}
                            />
                          )}
                          {sellPct > 0 && (
                            <div
                              className="bg-rose-400/80 transition-all"
                              style={{ width: `${sellPct}%` }}
                              title={`Sell: ${gradesConsensus.sell}`}
                            />
                          )}
                          {strongSellPct > 0 && (
                            <div
                              className="bg-rose-600 transition-all"
                              style={{ width: `${strongSellPct}%` }}
                              title={`Strong Sell: ${gradesConsensus.strongSell}`}
                            />
                          )}
                        </div>

                        {/* Legend */}
                        <div className="flex items-center justify-between gap-2 text-[8px] leading-tight flex-wrap">
                          {gradesConsensus.strongBuy > 0 && (
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-emerald-500" />
                              <span className="text-theme-secondary">{gradesConsensus.strongBuy} S.Buy</span>
                            </div>
                          )}
                          {gradesConsensus.buy > 0 && (
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-emerald-400/80" />
                              <span className="text-theme-secondary">{gradesConsensus.buy} Buy</span>
                            </div>
                          )}
                          {gradesConsensus.hold > 0 && (
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-amber-400" />
                              <span className="text-theme-secondary">{gradesConsensus.hold} Hold</span>
                            </div>
                          )}
                          {gradesConsensus.sell > 0 && (
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-rose-400/80" />
                              <span className="text-theme-secondary">{gradesConsensus.sell} Sell</span>
                            </div>
                          )}
                          {gradesConsensus.strongSell > 0 && (
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-rose-600" />
                              <span className="text-theme-secondary">{gradesConsensus.strongSell} S.Sell</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Right: Total Count */}
                <div className="flex flex-col items-end justify-center gap-0.5 shrink-0">
                  <div className="text-lg md:text-xl font-bold text-theme-primary leading-none">
                    {(gradesConsensus.strongBuy || 0) + (gradesConsensus.buy || 0) + (gradesConsensus.hold || 0) + (gradesConsensus.sell || 0) + (gradesConsensus.strongSell || 0)}
                  </div>
                  <div className="text-[8px] text-theme-muted uppercase tracking-wide leading-none">Ratings</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyHeader;
